import type React from 'react';
import { useCallback, useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import {
  getMoveInCostsTotalLimitError,
  isMoveInCostsNoteLengthValid,
  validateSecurityDepositInputs,
} from '../validation';
import userPersonas from '@common/constants/userPersonas';
import {
  AdornedInput,
  Box,
  Button,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Textarea,
} from '@zillow/constellation-10';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { CharacterCountFormHelp, useCharacterCount } from '@zg-rentals/visual-cohesion';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  securityDepositMandatesFromLease: Array<Record<string, string>>;
  editMode: boolean;
  fromDashboard: boolean;
  securityDepositPaymentAmount: number;
  securityDepositNotes: string;
  securityDepositStartsOpen: boolean;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  securityDeposit: number;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  currentPage: number;
  setupNewTerm: boolean;
  togglePrevPage: () => void;
}

const SecurityDepositPage: React.FC<Props> = ({
  saveStateToReduxStore,
  securityDepositMandatesFromLease,
  editMode = false,
  fromDashboard = false,
  securityDepositPaymentAmount,
  securityDepositNotes,
  securityDepositStartsOpen,
  moveInCostsPaymentLimits,
  totalPaymentAmount: propsTotalPaymentAmount,
  securityDeposit,
  individualLimit,
  pageVisited,
  currentProperty,
  user,
  onClose,
  currentPage,
  setupNewTerm,
  togglePrevPage,
}) => {
  const initialIsOpen =
    !editMode && !pageVisited?.has(1) && securityDeposit && securityDepositPaymentAmount === 0
      ? true
      : securityDepositStartsOpen;

  const initialPaymentAmount =
    !editMode && !pageVisited?.has(1) && securityDeposit && securityDepositPaymentAmount === 0
      ? String(centsToDollarsDecimal(securityDeposit))
      : securityDepositPaymentAmount === 0
        ? ''
        : String(centsToDollarsDecimal(securityDepositPaymentAmount));

  const hasAnsweredBefore = editMode || Boolean(pageVisited?.has(1));
  const [isOpen, setIsOpen] = useState<boolean | null>(initialIsOpen ? true : hasAnsweredBefore ? false : null);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(
    securityDepositPaymentAmount === 0
      ? propsTotalPaymentAmount
      : propsTotalPaymentAmount - securityDepositPaymentAmount,
  );
  const [moveInCosts, setMoveInCosts] = useState({
    moveInCostsPaymentType: 'Security Deposit',
    moveInCostsNotes: securityDepositNotes,
    moveInCostsPaymentAmount: initialPaymentAmount,
  });
  const [errors, setErrors] = useState({
    moveInCostsPaymentType: '',
    moveInCostsNotes: '',
    moveInCostsPaymentAmount: '',
  });

  const toggleOpen = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const skipSecurityDepositPage = useCallback(async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipSecurityDepositEvent({
      currentProperty,
      user,
    });

    const payload = {
      securityDepositSettings: {
        securityDepositPaymentAmount: 0,
        securityDepositNotes: '',
        securityDepositStartsOpen: false,
      },
    };

    await saveStateToReduxStore(payload);
  }, [currentProperty, user, saveStateToReduxStore]);

  const handleNotesChange = useCallback((e): void => {
    const key = e.target.dataset.statekey;
    setMoveInCosts((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  }, []);

  const { isFocused, onFocus, onBlur } = useCharacterCount();

  const handleAmountChange = useCallback((e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    const numericValue = removeNonDigit(value);

    setMoveInCosts((prev) => ({ ...prev, moveInCostsPaymentAmount: numericValue }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  }, []);

  const handleSaveStateToReduxStore = useCallback(
    async (e): Promise<any> => {
      e.preventDefault();

      const validInputs = validateSecurityDepositInputs({
        moveInCosts,
        individualLimit,
        handleErrors: (errors) => {
          setErrors(errors);
        },
      });

      if (!validInputs) {
        return;
      }

      const moveInCostsPaymentAmount = parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10);

      if (totalPaymentAmount + moveInCostsPaymentAmount > moveInCostsPaymentLimits) {
        setErrors((prev) => ({
          ...prev,
          moveInCostsPaymentAmount: getMoveInCostsTotalLimitError(moveInCostsPaymentLimits),
        }));
        return;
      }

      IreneOnboardingCSEvents.emitContinueFromSecurityDepositEvent({
        currentProperty,
        user,
      });

      const payload = {
        securityDepositSettings: {
          securityDepositPaymentAmount: parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10),
          securityDepositNotes: moveInCosts.moveInCostsNotes,
          securityDepositStartsOpen: isOpen === true,
        },
      };

      await saveStateToReduxStore(payload);
    },
    [
      moveInCosts,
      errors,
      totalPaymentAmount,
      isOpen,
      moveInCostsPaymentLimits,
      currentProperty,
      user,
      saveStateToReduxStore,
    ],
  );

  const getSecurityMandatesFromLease = useCallback(() => {
    return securityDepositMandatesFromLease.map(({ title, text }) => {
      return {
        question: title,
        answer: text.split('\n').map((innerText) => <p>{innerText}</p>),
      };
    });
  }, [securityDepositMandatesFromLease]);

  const renderOpenedSection = (): JSX.Element => {
    return (
      <>
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(errors.moveInCostsPaymentAmount)}
          required
          control={
            <AdornedInput
              fluid
              input={
                <AdornedInput.Input
                  type="number"
                  className="fs-exclude input-caret-hide"
                  value={!moveInCosts.moveInCostsPaymentAmount ? '' : moveInCosts.moveInCostsPaymentAmount}
                  onChange={handleAmountChange}
                  onBlur={handleAmountChange}
                  onFocus={handleAmountChange}
                  data-statekey="moveInCostsPaymentAmount"
                />
              }
              size="md"
              startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
            />
          }
          description={errors.moveInCostsPaymentAmount && <FormHelp>{errors.moveInCostsPaymentAmount}</FormHelp>}
          label={<Label>Amount</Label>}
        />

        <FormField
          css={{ maxWidth: '36rem' }}
          error={!isMoveInCostsNoteLengthValid(moveInCosts.moveInCostsNotes)}
          optional
          control={
            <Textarea
              data-statekey="moveInCostsNotes"
              className="fs-exclude"
              css={{ resize: 'vertical' }}
              value={moveInCosts.moveInCostsNotes}
              onChange={handleNotesChange}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          }
          description={
            <CharacterCountFormHelp
              currentLength={moveInCosts.moveInCostsNotes.length}
              maxLength={MOVE_IN_COSTS_NOTE_MAX_LENGTH}
              isFocused={isFocused}
              label="Note"
            />
          }
          label={<Label>Note</Label>}
        />
      </>
    );
  };

  const renderMainSection = (): JSX.Element => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <FieldSet required legend={<Legend size="lg">Would you like to collect a security deposit?</Legend>}>
          <LabeledControl
            control={<Radio name="security-deposit" value="yes" checked={isOpen === true} onChange={toggleOpen} />}
            label={<Label>Yes</Label>}
          />
          <LabeledControl
            control={
              <Radio
                name="security-deposit"
                value="no"
                checked={isOpen === false}
                onChange={(): void => setIsOpen(false)}
              />
            }
            label={<Label>No</Label>}
          />
        </FieldSet>
        {isOpen && renderOpenedSection()}
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={
        <FaqComponent
          faqs={[...getSecurityMandatesFromLease(), ...FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.SECURITY_DEPOSIT_PAGE]}
        />
      }
      onRequestClose={onClose}
      header={fromDashboard ? 'Move-in costs' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderMainSection()}
        </Box>
      }
      backAction={{ onClick: togglePrevPage, disabled: currentPage === 1 && setupNewTerm }}
      footerAriaLabel="Security deposit actions"
      footerEnd={
        <Button
          emphasis="filled"
          disabled={isOpen === null}
          onClick={isOpen ? handleSaveStateToReduxStore : skipSecurityDepositPage}
        >
          Continue
        </Button>
      }
    />
  );
};

export default SecurityDepositPage;
